import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Font, pdf } from '@react-pdf/renderer';
import { Document } from '@react-pdf/renderer';
import { useQueryClient } from '@tanstack/react-query';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { FOR_BANK_SECTIONS } from 'constants/businessToolSections/forBank';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { useBreakpoints } from 'hooks';
import { AboutTheCompany } from 'PDF/pages/AboutTheCompany/AboutTheCompany';
import { AboutTheCompanyPdf } from 'PDF/pages/AboutTheCompany/AboutTheCompanyPdf';
import { BusinessOperations, BusinessOperationsPdf } from 'PDF/pages/BusinessOperations';
import { CoverPagePdf } from 'PDF/pages/CoverPage';
import { CoverPage } from 'PDF/pages/CoverPage/CoverPage';
import { FinancialPlan, FinancialPlanPdf } from 'PDF/pages/FinancialPlan';
import { ImplementationPlan, ImplementationPlanPdf } from 'PDF/pages/ImplementationPlan';
import { LastPage, LastPagePdf } from 'PDF/pages/LastPage';
import { LegalFrameworkAndRisks, LegalFrameworkAndRisksPdf } from 'PDF/pages/LegalFrameworkAndRisks';
import { ManagementAndOrganization, ManagementAndOrganizationPdf } from 'PDF/pages/ManagementAndOrganization';
import { MarketAnalysis, MarketAnalysisPdf } from 'PDF/pages/MarketAnalysis';
import { MarketingAndSales } from 'PDF/pages/MarketingAndSales/MarketingAndSales';
import { MarketingAndSalesPdf } from 'PDF/pages/MarketingAndSales/MarketingAndSalesPdf';
import { ProductsAndServices } from 'PDF/pages/ProductsAndServices';
import { ProductsAndServicesPdf } from 'PDF/pages/ProductsAndServices/ProductsAndServicesPdf';
import { RaisingAndAllocatingFunds, RaisingAndAllocatingFundsPdf } from 'PDF/pages/RaisingAndAllocatingFunds';
import { Summary } from 'PDF/pages/Summary';
import { SummaryPdf } from 'PDF/pages/Summary/SummaryPdf';
import { PrivateSectionsEnum } from 'PDF/pages/TableOfContents/helpers';
import { TableOfContentsPdf } from 'PDF/pages/TableOfContents/TableOfContentsPdf';
import {
  AboutCompanyKeys,
  BusinessOperationsKeys,
  FinancialPlanKeys,
  ImplementationPlanKeys,
  LegalFrameworkAndRisksKeys,
  ManagementAndOrganizationKeys,
  MarketAnalysisKeys,
  MarketingAndSalesKeys,
  ProductsAndServicesKeys,
  RaisingAndAllocatingFundsKeys,
  SectionsAvailability,
  SectionsAvailabilityKeys,
  SectionsType,
  SummaryKeys,
} from 'PDF/types/pdfSections';
import { businessPlanQueryKeys } from 'services/businessPlan/businessPlan-qk';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useGetIndustryPictures } from 'services/businessPlan/hooks/useGetIndustryPictures';
import { IBusinessPlanResponse, INDUSTRY_TYPE } from 'services/businessPlan/types';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getBusinessPlanChapters, getSetBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { ReactComponent as DownloadIcon } from 'assets/icons/DownloadIcon.svg';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useExamplePreview } from 'hooks/useExamplePreview';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { OTHER_SERVICES_SECTOR } from 'components/BusinessToolForm/constants/industrySectorOptions';
import { GLOBAL_FIELD_NAMES } from 'components/BusinessToolForm/Sections/Global/form';
import { DocDownloadProgressModal } from 'components/DocDownloadProgressModal/DocDownloadProgressModal';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { pdfWordSplitter } from 'helpers/pdfWordSplitter';
import ToastService from 'helpers/ToastService';

import { IPdfDownloadButtonProps } from './types';
import { useAIActionHandler } from '../components/BusinessToolAiProvider/hooks/useAIActionHandler';
import { getSectionNumber, getSectionsAvailability, transformSectionPictures } from '../PdfPreview/utils';

Font.registerHyphenationCallback(pdfWordSplitter);

export const PdfDownloadButton = forwardRef<HTMLButtonElement, IPdfDownloadButtonProps>(
  ({ className, onFileUpload, triggerFromShare }, ref) => {
    const { id: businessPlanId } = useParams(); // Extracts '97b9f8fe-0f10-4b4d-b6e2-62445f0cfee5'
    const authUser = useUserStore(getAuthUser);

    const [loading, setLoading] = useState<boolean>(false);
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const { t } = useTranslation();
    const updateMutation = useUpdateBusinessPlan();

    const userSubscription = useUserSubscriptionStore(getUserSubscription);
    const isStandardOrLightTariff =
      userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
      userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

    const queryClient = useQueryClient();
    const data = queryClient.getQueryState<IBusinessPlanResponse>(businessPlanQueryKeys.businessPlan(businessPlanId!));
    const isDocumentOwner = data?.data?.createdBy === authUser?.email;
    const allowedPdfCreation = (isDocumentOwner && isStandardOrLightTariff) || !isDocumentOwner;

    const businessPlan = useBusinessToolStore(getBusinessPlan);
    const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
    const { handleActionWithAICheck } = useAIActionHandler();

    const isInvestors = businessPlan.type === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
    const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
    const companySector = businessPlanChapters.GLOBAL[GLOBAL_FIELD_NAMES.COMPANY_SECTOR];
    const sector =
      typeof companySector === 'object' && companySector !== null ? companySector.value : OTHER_SERVICES_SECTOR;
    const { data: industryPictures } = useGetIndustryPictures({ type: INDUSTRY_TYPE[sector], enabled: true });
    const sectionPictures = transformSectionPictures(industryPictures);

    const canAccessProductAndServices =
      useCheckSubSectionAccess(FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES) && !isInvestors;
    const canAccessMarketAnalysis = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKET_ANALYSIS);
    const canAccessFinancialPlan = useCheckSubSectionAccess(FOR_BANK_SECTIONS.FINANCIAL_PLAN);
    const canAccessMarketingAndSales = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKETING_AND_SALES);
    const canAccessBusinessOperations = useCheckSubSectionAccess(FOR_BANK_SECTIONS.BUSINESS_OPERATIONS);
    const canAccessManagementAndOrganization = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION);
    const canAccessLegalFrameworkAndRisks =
      useCheckSubSectionAccess(FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS) && !isInvestors;
    const canAccessImplementationPlan = useCheckSubSectionAccess(FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN) && !isInvestors;
    const canAccessRaisingAndAllocatingFunds = useCheckSubSectionAccess(FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS);

    const canAccessMarketAnalysisInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS);
    const canAccessMarketingAndSalesInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.MARKETING_AND_SALES);
    const canAccessBusinessOperationsInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.BUSINESS_OPERATIONS);
    const canAccessManagementAndOrganizationInvestor = useCheckSubSectionAccess(
      FOR_INVESTORS_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
    );
    const canAccessFinancialPlanInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.FINANCIAL_PLAN);
    const canAccessRaisingAndAllocatingFundsInvestor = useCheckSubSectionAccess(
      FOR_INVESTORS_SECTIONS.RAISING_AND_ALLOCATING_FUNDS,
    );

    const { isExamplePage } = useExamplePreview();

    const privateSections = {
      [PrivateSectionsEnum.PRODUCT_AND_SERVICES]: canAccessProductAndServices,
      [PrivateSectionsEnum.MARKET_ANALYSIS]: canAccessMarketAnalysis || canAccessMarketAnalysisInvestor,
      [PrivateSectionsEnum.FINANCIAL_PLAN]: canAccessFinancialPlan || canAccessFinancialPlanInvestor,
      [PrivateSectionsEnum.BUSINESS_OPERATIONS]: canAccessBusinessOperations || canAccessBusinessOperationsInvestor,
      [PrivateSectionsEnum.MARKETING_AND_SALES]: canAccessMarketingAndSales || canAccessMarketingAndSalesInvestor,
      [PrivateSectionsEnum.LEGAL_FRAMEWORK_AND_RISKS]: canAccessLegalFrameworkAndRisks,
      [PrivateSectionsEnum.IMPLEMENTATION_PLAN]: canAccessImplementationPlan,
      [PrivateSectionsEnum.MANAGEMENT_AND_ORGANIZATION]:
        canAccessManagementAndOrganization || canAccessManagementAndOrganizationInvestor,
      [PrivateSectionsEnum.RAISING_AND_ALLOCATING_FUNDS]:
        canAccessRaisingAndAllocatingFunds || canAccessRaisingAndAllocatingFundsInvestor,
    };
    const initialSections: SectionsType = {
      coverPage: null,
      summary: undefined,
      summaryInvestor: undefined,
      aboutCompany: undefined,
      aboutCompanyInvestor: undefined,
      productAndServices: undefined,
      marketAnalysis: undefined,
      marketAnalysisInvestor: undefined,
      businessOperations: undefined,
      businessOperationsInvestor: undefined,
      managementAndOrganization: undefined,
      managementAndOrganizationInvestor: undefined,
      legalFrameworkAndRisks: undefined,
      implementationPlan: undefined,
      financialPlan: undefined,
      financialPlanInvestor: undefined,
      marketingAndSales: undefined,
      marketingAndSalesInvestor: undefined,
      raisingAndAllocatingFunds: undefined,
      raisingAndAllocatingFundsInvestor: undefined,
      lastPage: null,
    };

    if (businessPlan.type === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
      initialSections.summaryInvestor = null;
      initialSections.aboutCompanyInvestor = null;
      initialSections.marketAnalysisInvestor = canAccessMarketAnalysisInvestor ? null : undefined;
      initialSections.marketingAndSalesInvestor = canAccessMarketingAndSalesInvestor ? null : undefined;
      initialSections.businessOperationsInvestor = canAccessBusinessOperationsInvestor ? null : undefined;
      initialSections.managementAndOrganizationInvestor = canAccessManagementAndOrganizationInvestor ? null : undefined;
      initialSections.raisingAndAllocatingFundsInvestor = canAccessRaisingAndAllocatingFundsInvestor ? null : undefined;
      initialSections.financialPlanInvestor = canAccessFinancialPlanInvestor ? null : undefined;
    } else {
      initialSections.summary = null;
      initialSections.aboutCompany = null;
      initialSections.productAndServices = canAccessProductAndServices ? null : undefined;
      initialSections.marketAnalysis = canAccessMarketAnalysis ? null : undefined;
      initialSections.marketingAndSales = canAccessMarketingAndSales ? null : undefined;
      initialSections.businessOperations = canAccessBusinessOperations ? null : undefined;
      initialSections.managementAndOrganization = canAccessManagementAndOrganization ? null : undefined;
      ((initialSections.implementationPlan = canAccessImplementationPlan ? null : undefined),
        (initialSections.legalFrameworkAndRisks = canAccessLegalFrameworkAndRisks ? null : undefined),
        (initialSections.raisingAndAllocatingFunds = canAccessRaisingAndAllocatingFunds ? null : undefined));
      initialSections.financialPlan = canAccessFinancialPlan ? null : undefined;
    }

    const [sections, setSections] = useState<SectionsType>(initialSections);
    const [isSectionsReady, setIsSectionsReady] = useState<boolean>(false);

    const handleSetSectionsProps = useCallback((key: string, props: Record<string, any>) => {
      setSections((prevSections) => ({ ...prevSections, [key]: props }));
    }, []);

    const handleButtonClick = () =>
      handleActionWithAICheck(async () => {
        if (!triggerFromShare && !isStandardOrLightTariff) {
          return;
        }
        setLoading(true);
        setPdfUrl('');
        setSections(initialSections);
        setIsSectionsReady(false);
      });

    useEffect(() => {
      const generatePdf = async (sectionsAvailability: SectionsAvailability) => {
        const summaryFilledKeys = (Object.keys(sectionsAvailability.summary) as SummaryKeys[]).filter(
          (key) => !!sectionsAvailability.summary[key],
        );
        const aboutCompanyFilledKeys = (Object.keys(sectionsAvailability.aboutCompany) as AboutCompanyKeys[]).filter(
          (key) => !!sectionsAvailability.aboutCompany[key],
        );
        const productsAndServicesFilledKeys = (
          Object.keys(sectionsAvailability.productsAndServices) as ProductsAndServicesKeys[]
        ).filter((key) => !!sectionsAvailability.productsAndServices[key]);
        const marketAnalysisFilledKeys = (
          Object.keys(sectionsAvailability.marketAnalysis) as MarketAnalysisKeys[]
        ).filter((key) => !!sectionsAvailability.marketAnalysis[key]);
        const marketingAndSalesFilledKeys = (
          Object.keys(sectionsAvailability.marketingAndSales) as MarketingAndSalesKeys[]
        ).filter((key) => !!sectionsAvailability.marketingAndSales[key]);
        const businessOperationsFilledKeys = (
          Object.keys(sectionsAvailability.businessOperations) as BusinessOperationsKeys[]
        ).filter((key) => !!sectionsAvailability.businessOperations[key]);
        const managementAndOrganizationFilledKeys = (
          Object.keys(sectionsAvailability.managementAndOrganization) as ManagementAndOrganizationKeys[]
        ).filter((key) => !!sectionsAvailability.managementAndOrganization[key]);
        const implementationPlanFilledKeys = (
          Object.keys(sectionsAvailability.implementationPlan) as ImplementationPlanKeys[]
        ).filter((key) => !!sectionsAvailability.implementationPlan[key]);
        const raisingAndAllocatingFundsFilledKeys = (
          Object.keys(sectionsAvailability.raisingAndAllocatingFunds) as RaisingAndAllocatingFundsKeys[]
        ).filter((key) => !!sectionsAvailability.raisingAndAllocatingFunds[key]);
        const legalFrameworkAndRisksFilledKeys = (
          Object.keys(sectionsAvailability.legalFrameworkAndRisks) as LegalFrameworkAndRisksKeys[]
        ).filter((key) => !!sectionsAvailability.legalFrameworkAndRisks[key]);
        const financialPlanFilledKeys = (Object.keys(sectionsAvailability.financialPlan) as FinancialPlanKeys[]).filter(
          (key) => !!sectionsAvailability.financialPlan[key],
        );
        const sectionsData = {
          summary: !!summaryFilledKeys.length,
          aboutCompany: !!aboutCompanyFilledKeys.length,
          productsAndServices: !!productsAndServicesFilledKeys.length,
          marketAnalysis: !!marketAnalysisFilledKeys.length,
          marketingAndSales: !!marketingAndSalesFilledKeys.length,
          businessOperations: !!businessOperationsFilledKeys.length,
          managementAndOrganization: !!managementAndOrganizationFilledKeys.length,
          implementationPlan: !!implementationPlanFilledKeys.length,
          raisingAndAllocatingFunds: !!raisingAndAllocatingFundsFilledKeys.length,
          legalFrameworkAndRisks: !!legalFrameworkAndRisksFilledKeys.length,
          financialPlan: !!financialPlanFilledKeys.length,
        };
        const filledSections = (Object.keys(sectionsData) as SectionsAvailabilityKeys[]).filter(
          (key) => !!sectionsData[key],
        );

        try {
          const pdfBlob = await pdf(
            <Document>
              <CoverPagePdf
                {...sections.coverPage}
                sectionPicture={sectionPictures?.Title}
                isStandardOrLightTariff={isStandardOrLightTariff}
              />
              <TableOfContentsPdf
                privateSections={privateSections}
                businessPlanType={businessPlan.type}
                sectionPicture={sectionPictures?.Contents}
                sectionsAvailability={sectionsAvailability}
                openingBalanceSheetData={businessPlanChapters.FINANCIAL_PLAN.openingBalanceSheets}
                isStandardOrLightTariff={isStandardOrLightTariff}
                isExamplePage={isExamplePage}
              />
              {businessPlan.type === BUSINESS_PLAN_TYPES.FOR_INVESTORS ? (
                <>
                  <SummaryPdf
                    sectionPicture={sectionPictures?.Section1}
                    {...sections.summaryInvestor}
                    businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                    availability={sectionsAvailability.summary}
                    sectionNumber={getSectionNumber(filledSections, 'summary')}
                    isStandardOrLightTariff={isStandardOrLightTariff}
                    isExamplePage={isExamplePage}
                  />
                  <AboutTheCompanyPdf
                    sectionPicture={sectionPictures?.Section2}
                    {...sections.aboutCompanyInvestor}
                    businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                    availability={sectionsAvailability.aboutCompany}
                    sectionNumber={getSectionNumber(filledSections, 'aboutCompany')}
                    isStandardOrLightTariff={isStandardOrLightTariff}
                    isExamplePage={isExamplePage}
                  />
                  {canAccessMarketAnalysisInvestor && (
                    <MarketAnalysisPdf
                      sectionPicture={sectionPictures?.Section4}
                      {...sections.marketAnalysisInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.marketAnalysis}
                      sectionNumber={getSectionNumber(filledSections, 'marketAnalysis')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessMarketingAndSalesInvestor && (
                    <MarketingAndSalesPdf
                      sectionPicture={sectionPictures?.Section5}
                      {...sections.marketingAndSalesInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.marketingAndSales}
                      sectionNumber={getSectionNumber(filledSections, 'marketingAndSales')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessBusinessOperationsInvestor && (
                    <BusinessOperationsPdf
                      sectionPicture={sectionPictures?.Section6}
                      {...sections.businessOperationsInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.businessOperations}
                      sectionNumber={getSectionNumber(filledSections, 'businessOperations')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessManagementAndOrganizationInvestor && (
                    <ManagementAndOrganizationPdf
                      sectionPicture={sectionPictures?.Section7}
                      {...sections.managementAndOrganizationInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.managementAndOrganization}
                      sectionNumber={getSectionNumber(filledSections, 'managementAndOrganization')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessRaisingAndAllocatingFundsInvestor && (
                    <RaisingAndAllocatingFundsPdf
                      sectionPicture={sectionPictures?.Section10}
                      {...sections.raisingAndAllocatingFundsInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.raisingAndAllocatingFunds}
                      sectionNumber={getSectionNumber(filledSections, 'raisingAndAllocatingFunds')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessFinancialPlanInvestor && (
                    <FinancialPlanPdf
                      sectionPicture={sectionPictures?.Section11}
                      {...sections.financialPlanInvestor}
                      businessPlanType={BUSINESS_PLAN_TYPES.FOR_INVESTORS}
                      availability={sectionsAvailability.financialPlan}
                      sectionNumber={getSectionNumber(filledSections, 'financialPlan')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                </>
              ) : (
                <>
                  <SummaryPdf
                    sectionPicture={sectionPictures?.Section1}
                    {...sections.summary}
                    availability={sectionsAvailability.summary}
                    sectionNumber={getSectionNumber(filledSections, 'summary')}
                    isStandardOrLightTariff={isStandardOrLightTariff}
                    isExamplePage={isExamplePage}
                  />
                  <AboutTheCompanyPdf
                    sectionPicture={sectionPictures?.Section2}
                    {...sections.aboutCompany}
                    availability={sectionsAvailability.aboutCompany}
                    sectionNumber={getSectionNumber(filledSections, 'aboutCompany')}
                    isStandardOrLightTariff={isStandardOrLightTariff}
                    isExamplePage={isExamplePage}
                  />
                  {canAccessProductAndServices && (
                    <ProductsAndServicesPdf
                      sectionPicture={sectionPictures?.Section3}
                      {...sections.productAndServices}
                      availability={sectionsAvailability.productsAndServices}
                      sectionNumber={getSectionNumber(filledSections, 'productsAndServices')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessMarketAnalysis && (
                    <MarketAnalysisPdf
                      sectionPicture={sectionPictures?.Section4}
                      {...sections.marketAnalysis}
                      availability={sectionsAvailability.marketAnalysis}
                      sectionNumber={getSectionNumber(filledSections, 'marketAnalysis')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessMarketingAndSales && (
                    <MarketingAndSalesPdf
                      sectionPicture={sectionPictures?.Section5}
                      {...sections.marketingAndSales}
                      availability={sectionsAvailability.marketingAndSales}
                      sectionNumber={getSectionNumber(filledSections, 'marketingAndSales')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessBusinessOperations && (
                    <BusinessOperationsPdf
                      sectionPicture={sectionPictures?.Section6}
                      {...sections.businessOperations}
                      availability={sectionsAvailability.businessOperations}
                      sectionNumber={getSectionNumber(filledSections, 'businessOperations')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessManagementAndOrganization && (
                    <ManagementAndOrganizationPdf
                      sectionPicture={sectionPictures?.Section7}
                      {...sections.managementAndOrganization}
                      availability={sectionsAvailability.managementAndOrganization}
                      sectionNumber={getSectionNumber(filledSections, 'managementAndOrganization')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessImplementationPlan && (
                    <ImplementationPlanPdf
                      sectionPicture={sectionPictures?.Section8}
                      {...sections.implementationPlan}
                      availability={sectionsAvailability.implementationPlan}
                      sectionNumber={getSectionNumber(filledSections, 'implementationPlan')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessRaisingAndAllocatingFunds && (
                    <RaisingAndAllocatingFundsPdf
                      sectionPicture={sectionPictures?.Section10}
                      {...sections.raisingAndAllocatingFunds}
                      availability={sectionsAvailability.raisingAndAllocatingFunds}
                      sectionNumber={getSectionNumber(filledSections, 'raisingAndAllocatingFunds')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessLegalFrameworkAndRisks && (
                    <LegalFrameworkAndRisksPdf
                      sectionPicture={sectionPictures?.Section9}
                      {...sections.legalFrameworkAndRisks}
                      availability={sectionsAvailability.legalFrameworkAndRisks}
                      sectionNumber={getSectionNumber(filledSections, 'legalFrameworkAndRisks')}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                  {canAccessFinancialPlan && (
                    <FinancialPlanPdf
                      sectionPicture={sectionPictures?.Section11}
                      {...sections.financialPlan}
                      availability={sectionsAvailability.financialPlan}
                      sectionNumber={getSectionNumber(filledSections, 'financialPlan')}
                      openingBalanceSheetData={businessPlanChapters.FINANCIAL_PLAN.openingBalanceSheets}
                      isStandardOrLightTariff={isStandardOrLightTariff}
                      isExamplePage={isExamplePage}
                    />
                  )}
                </>
              )}
              {sections.lastPage?.contactData && (
                <LastPagePdf
                  {...sections.lastPage}
                  sectionPicture={sectionPictures?.Backside}
                  isStandardOrLightTariff={isStandardOrLightTariff}
                />
              )}
            </Document>,
          ).toBlob();
          const url = URL.createObjectURL(pdfBlob);
          setBusinessPlan({
            ...businessPlan,
            metadata: {
              ...getValidatedMetadata(businessPlan.metadata),
              pdfDocShortLink: url,
            },
          });
          if (!triggerFromShare) {
            const params = {
              id: businessPlan.uuid,
              body: {
                ...businessPlan,
                metadata: JSON.stringify({
                  ...getValidatedMetadata(businessPlan.metadata),
                  downloadDocumentCount: (businessPlan.metadata.downloadDocumentCount ?? 0) + 1,
                }),
              },
            };
            updateMutation.mutateAsync(params);
          }

          setPdfUrl(url);
          setLoading(false);
        } catch {
          ToastService.showError('PDF generation error');
          setLoading(false);
        }
      };

      if (!isSectionsReady && !Object.values(sections).includes(null) && industryPictures) {
        const sectionsAvailability = getSectionsAvailability(sections, businessPlan.type, privateSections);

        setIsSectionsReady(true);
        generatePdf(sectionsAvailability);
      }
    }, [sections]);
    const { isMobile } = useBreakpoints();

    useEffect(() => {
      if (!pdfUrl) return;

      if (onFileUpload) {
        onFileUpload(pdfUrl);
      } else {
        if (isMobile) {
          window.location.href = pdfUrl;
        } else {
          window.open(pdfUrl, '_blank');
        }
      }
    }, [pdfUrl]);

    return (
      <>
        {loading && <DocDownloadProgressModal isModalOpened={loading} modalText={t('pdfModalTitle')} />}
        <Button
          ref={ref}
          variant="outlined"
          size="large"
          endIcon={<DownloadIcon />}
          onClick={handleButtonClick}
          className={className}
        >
          PDF
        </Button>

        <>
          {loading && !isSectionsReady && (
            <>
              {!sections.coverPage && <CoverPage saveSectionProps={handleSetSectionsProps} />}
              {!sections.summary && (
                <Summary saveSectionProps={handleSetSectionsProps} sectionPicture={sectionPictures?.Section1} />
              )}
              {!sections.aboutCompany && (
                <AboutTheCompany saveSectionProps={handleSetSectionsProps} sectionPicture={sectionPictures?.Section2} />
              )}
              {!sections.productAndServices && canAccessProductAndServices && (
                <ProductsAndServices
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section3}
                />
              )}
              {((!sections.marketAnalysis && canAccessMarketAnalysis) ||
                (!sections.marketAnalysisInvestor && canAccessMarketAnalysisInvestor)) && (
                <MarketAnalysis saveSectionProps={handleSetSectionsProps} sectionPicture={sectionPictures?.Section4} />
              )}
              {((!sections.marketingAndSales && canAccessMarketingAndSales) ||
                (!sections.marketingAndSalesInvestor && canAccessMarketingAndSalesInvestor)) && (
                <MarketingAndSales
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section5}
                  isExamplePage={isExamplePage}
                />
              )}
              {((!sections.businessOperations && canAccessBusinessOperations) ||
                (!sections.businessOperationsInvestor && canAccessBusinessOperationsInvestor)) && (
                <BusinessOperations
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section6}
                />
              )}
              {((!sections.managementAndOrganization && canAccessManagementAndOrganization) ||
                (!sections.managementAndOrganizationInvestor && canAccessManagementAndOrganizationInvestor)) && (
                <ManagementAndOrganization
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section7}
                />
              )}
              {!sections.implementationPlan && canAccessImplementationPlan && (
                <ImplementationPlan
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section8}
                />
              )}
              {!sections.legalFrameworkAndRisks && canAccessLegalFrameworkAndRisks && (
                <LegalFrameworkAndRisks
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section9}
                />
              )}
              {((!sections.raisingAndAllocatingFunds && canAccessRaisingAndAllocatingFunds) ||
                (!sections.raisingAndAllocatingFundsInvestor && canAccessRaisingAndAllocatingFundsInvestor)) && (
                <RaisingAndAllocatingFunds
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section10}
                  isExamplePage={isExamplePage}
                />
              )}
              {((!sections.financialPlan && canAccessFinancialPlan) ||
                (!sections.financialPlanInvestor && canAccessFinancialPlanInvestor)) && (
                <FinancialPlan
                  saveSectionProps={handleSetSectionsProps}
                  sectionPicture={sectionPictures?.Section11}
                  isExamplePage={isExamplePage}
                />
              )}
              {!sections.lastPage && <LastPage saveSectionProps={handleSetSectionsProps} />}
            </>
          )}
        </>
      </>
    );
  },
);
